import Link from 'next/link'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors } from '@/styles'

import { SectionWrapper } from '../elements'
import AccordionWrapper from './accordion-wrapper'
import ContentComponent, { ItemLink } from './content'
import { FaqAnswer } from './types'

const Wrapper = styled(SectionWrapper)`
  display: flex;
  flex-direction: row;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 48px;
  width: 302px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
  }
`

const AccordionOuterWrapper = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 32px;
  }
`

const Title = styled.h2<{ textAlign?: string }>`
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 48px;
  color: #111827;
  text-align: ${props => props.textAlign ?? 'left'};
  margin: 0 0 16px 0;
  width: 100%;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 32px;
    line-height: 40px;
    margin: 0;
  }
`

const Text = styled.p`
  font-family: Inter, sans-serif;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  color: #111827;

  span {
    display: block;
  }

  a {
    text-decoration: underline;
    color: ${colors.link};
  }
`

const StyledLink = styled.a`
  text-decoration: none;
  color: ${colors.link};
`

const FAQs = () => {
  const { t } = useTranslation('landlords')

  const faqData = [
    {
      titleId: 'faq-cost',
      title: t('landlords:How much does it cost to list on liv.rent?'),
      answers: [
        {
          steps: [
            <Trans
              key="It is currently free to list as many properties"
              i18nKey="landlords:It is currently free to list as many properties"
            >
              It is currently free to list as many properties as you manage on
              liv.rent with our Essentials plan. However, chances are if you
              have more than 3 properties, you will greatly benefit from signing
              up for either our{' '}
              <ItemLink href="/pricing">Growth or Business plan</ItemLink>
              .
              <br key="breakline" />
              <br key="breakline" />
              Not ready to sign up? Book an{' '}
              <ItemLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://calendly.com/livrent/30min-educational-session?utm_source=livrent&utm_medium=landingpage&utm_campaign=landlords_main"
              >
                informational session
              </ItemLink>{' '}
              instead and speak with our team members about how liv.rent can
              help you market and manage your listings.
            </Trans>
          ]
        }
      ]
    },
    {
      titleId: 'faq-advertise',
      title: t('landlords:How do you advertise a rental property?'),
      answers: [
        {
          steps: [
            <Trans
              key="Using liv.rent is the best way to get your rental property seen by quality tenants faster."
              i18nKey="landlords:Using liv.rent is the best way to get your rental property seen by quality tenants faster."
            >
              Using <ItemLink href="https://liv.rent/">liv.rent</ItemLink> is
              the best way to get your rental property seen by quality tenants
              faster. Here’s how landlords and property managers can get their
              listing advertised for free:
            </Trans>
          ]
        },
        {
          heading: t('landlords:Advertise online'),
          steps: [
            t(
              'landlords:The best way to advertise your rental property is online. Although it is common to post on traditional classified sites, you are much better off posting on a dedicated rental property listing site such as '
            ),
            {
              text: t('landlords:liv.rent'),
              href: '/'
            },
            t('landlords:. With a built-in '),
            {
              text: t('landlords:verification process'),
              href: 'https://liv.rent/blog/2019/10/verification-process/'
            },
            t(
              'landlords:, you have access to a pool of pre-screened tenants. Plus, you can evaluate the credibility of potential tenants based on their '
            ),
            {
              text: t('landlords:Trust Score'),
              href: 'https://liv.rent/blog/2019/10/trust-score-explained/'
            },
            t(
              'landlords: our intelligent rating system powered by an algorithm that corroborates submitted tenant information.'
            )
          ]
        },
        {
          heading: t('landlords:Promote through social media'),
          steps: [
            {
              text: 'Marketing rental apartments',
              href: 'https://liv.rent/blog/2019/04/using-social-media-to-market-your-rental/'
            },
            t('landlords: on social media is a must. For example, '),
            {
              text: t('landlords:Facebook Rental Groups'),
              href: 'https://www.facebook.com/groups/livrentlistings/'
            },
            t(
              "landlords: and Facebook Marketplace are excellent channels to increase exposure to your rental listing. Make sure that the link to your rental listing has been optimized for social sharing (i.e. your link contains a proper thumbnail image, title, and description). Certain rental property listing sites have syndicated channels that allow you to cross-promote. For example, you can easily share your rental listing to liv.rent's syndicated channels including Facebook Marketplace, Facebook Rental Groups, WeChat, as well as Craigslist, Kijiji, Zumper and more."
            )
          ]
        },
        {
          heading: t(
            'landlords:Write an advertisement tailored to your target audience'
          ),
          steps: [
            t(
              'landlords:Advertisement tailored to target audience',
              'Create a persona that reflects your ideal tenant, and identify key points that would attract this type of person. In your advertisement, make sure to highlight the unique features of your property in your introduction paragraph and provide the following information: location, amenities, utilities, pet policy, price, move-in date, lease type. Most importantly, always include '
            ),
            {
              text: t('landlords:quality photos'),
              href: 'https://liv.rent/blog/2018/09/tips-for-photographing-your-rental-property/'
            }
          ]
        },
        {
          heading: t(
            'landlords:Showcase your rental property with video tours'
          ),
          steps: [
            t('landlords:Uploading '),
            {
              text: t('landlords:rental listing video tours'),
              href: 'https://liv.rent/blog/2020/05/feature-update-view-rental-listings-online-with-video-tours/'
            },
            t(
              'landlords: is another way to get seen. In addition to giving prospective tenants a better spatial understanding of your property, it also helps to reduce the number of in-person viewings required, freeing up more of your time.'
            )
          ]
        },
        {
          heading: t('landlords:Price competitively'),
          steps: [
            t('landlords:Pricing is key to attracting the '),
            {
              text: t('landlords:right tenant'),
              href: 'https://liv.rent/blog/2018/10/how-to-pick-the-right-tenant'
            },
            t(
              "landlords: and keeping your tenant turnover low. As a result, it's important to research beforehand and find out the average rental rate in the market. To save you time from having to do the research, liv.rent's "
            ),
            {
              text: t('landlords:landlord dashboard'),
              href: 'https://liv.rent/blog/2020/10/landlord-dashboard-digital-tools-for-landlords-property-managers/'
            },
            t(
              'landlords: will show you the average rent for a comparable property and how your pricing compares to similar listings.'
            )
          ]
        },
        {
          heading: t(
            'landlords:Advertise your property through offline promotion'
          ),
          steps: [
            t(
              'landlords:Depending on who your prospective tenants are, you can place a rental ad in newspapers. Other options include local bulletin boards (e.g. universities, grocery stores, community centers), word of mouth, and posting a "For Rent" sign out on your property.'
            )
          ]
        }
      ]
    },
    {
      titleId: 'faq-personalinfo',
      title: t(
        'landlords:Why does liv.rent ask for so much personal information?'
      ),
      answers: [
        {
          steps: [
            <Trans
              key="The information we ask for is exactly what you’ll find on a standard tenancy application. We don’t ask for anything that you wouldn’t share with a landlord or tenant if you wanted to get into a rental agreement. We take your"
              i18nKey="landlords:The information we ask for is exactly what you’ll find on a standard tenancy application. We don’t ask for anything that you wouldn’t share with a landlord or tenant if you wanted to get into a rental agreement. We take your"
            >
              The information we ask for is exactly what you’ll find on a
              standard tenancy application. We don’t ask for anything that you
              wouldn’t share with a landlord or tenant if you wanted to get into
              a rental agreement. We take your{' '}
              <ItemLink href="/privacy#personal-information">
                personal information
              </ItemLink>{' '}
              very seriously.
            </Trans>
          ]
        }
      ]
    }
  ]

  return (
    <Wrapper>
      <TextWrapper>
        <Title>{t('FAQ')}</Title>
        <Text>
          {t('landlords:Any more questions?')}{' '}
          <span>
            {t('landlords:Visit our')}{' '}
            <Link href="/faq" passHref legacyBehavior>
              <StyledLink id="landlord-faqpage-click">
                {t('landlords:FAQ Page')}
              </StyledLink>
            </Link>
          </span>
        </Text>
      </TextWrapper>
      <AccordionOuterWrapper>
        {faqData.map(({ titleId, title, answers }, index) => {
          const faqAnswers = answers as FaqAnswer[]
          return (
            <AccordionWrapper
              key={`landlords-faq-${index}`}
              titleId={`landlord-${titleId}-click`}
              title={title}
              content={<ContentComponent content={{ answers: faqAnswers }} />}
            />
          )
        })}
      </AccordionOuterWrapper>
    </Wrapper>
  )
}

export default FAQs
